import { useState, useEffect } from "react";
import { useAuth } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Users, 
  Wallet, 
  Calculator, 
  TrendingUp, 
  TrendingDown,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowUpRight,
  Building2
} from "lucide-react";

const DashboardHome = () => {
  const { user, API, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
        // Use mock data if API fails
        setStats({
          total_contribuyentes: 1250,
          recaudacion_mes: 45678.90,
          liquidaciones_pendientes: 89,
          pagos_hoy: 23,
          tasa_morosidad: 12.5,
          licencias_activas: 456
        });
      }
      setLoading(false);
    };
    fetchStats();
  }, [API, token]);

  const statCards = [
    {
      title: "Contribuyentes Registrados",
      value: stats?.total_contribuyentes?.toLocaleString() || "0",
      icon: Users,
      trend: "+12%",
      trendUp: true,
      color: "blue",
      description: "Total de contribuyentes activos"
    },
    {
      title: "Recaudación del Mes",
      value: `Bs. ${stats?.recaudacion_mes?.toLocaleString() || "0"}`,
      icon: Wallet,
      trend: "+8.5%",
      trendUp: true,
      color: "green",
      description: "Ingresos acumulados"
    },
    {
      title: "Liquidaciones Pendientes",
      value: stats?.liquidaciones_pendientes?.toString() || "0",
      icon: Calculator,
      trend: "-5%",
      trendUp: false,
      color: "yellow",
      description: "Por cobrar"
    },
    {
      title: "Pagos Hoy",
      value: stats?.pagos_hoy?.toString() || "0",
      icon: CheckCircle,
      trend: "+15%",
      trendUp: true,
      color: "emerald",
      description: "Transacciones del día"
    },
    {
      title: "Tasa de Morosidad",
      value: `${stats?.tasa_morosidad || 0}%`,
      icon: AlertCircle,
      trend: "-2%",
      trendUp: true,
      color: "red",
      description: "Contribuyentes morosos"
    },
    {
      title: "Licencias Activas",
      value: stats?.licencias_activas?.toString() || "0",
      icon: FileText,
      trend: "+3%",
      trendUp: true,
      color: "purple",
      description: "LAE vigentes"
    }
  ];

  const recentTransactions = [
    { id: "TRX-001", contribuyente: "Juan Pérez", concepto: "Impuesto Inmobiliario", monto: "Bs. 1,250.00", estado: "Completado", fecha: "Hoy 10:30" },
    { id: "TRX-002", contribuyente: "María García", concepto: "LAE - Renovación", monto: "Bs. 850.00", estado: "Completado", fecha: "Hoy 09:45" },
    { id: "TRX-003", contribuyente: "Carlos López", concepto: "Patente de Vehículo", monto: "Bs. 450.00", estado: "Pendiente", fecha: "Hoy 09:15" },
    { id: "TRX-004", contribuyente: "Ana Rodríguez", concepto: "Publicidad Comercial", monto: "Bs. 2,100.00", estado: "Completado", fecha: "Ayer 16:20" },
    { id: "TRX-005", contribuyente: "Pedro Martínez", concepto: "Impuesto Inmobiliario", monto: "Bs. 980.00", estado: "Vencido", fecha: "Ayer 14:10" }
  ];

  const getStatusBadge = (estado) => {
    const styles = {
      Completado: "bg-green-100 text-green-700 border-green-200",
      Pendiente: "bg-yellow-100 text-yellow-700 border-yellow-200",
      Vencido: "bg-red-100 text-red-700 border-red-200"
    };
    return styles[estado] || styles.Pendiente;
  };

  const getCardColor = (color) => {
    const colors = {
      blue: "border-b-4 border-b-blue-600",
      green: "border-b-4 border-b-green-600",
      yellow: "border-b-4 border-b-yellow-500",
      emerald: "border-b-4 border-b-emerald-500",
      red: "border-b-4 border-b-red-500",
      purple: "border-b-4 border-b-purple-500"
    };
    return colors[color] || colors.blue;
  };

  const getIconBg = (color) => {
    const colors = {
      blue: "bg-blue-100 text-blue-600",
      green: "bg-green-100 text-green-600",
      yellow: "bg-yellow-100 text-yellow-600",
      emerald: "bg-emerald-100 text-emerald-600",
      red: "bg-red-100 text-red-600",
      purple: "bg-purple-100 text-purple-600"
    };
    return colors[color] || colors.blue;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-900 rounded-xl p-6 text-white" data-testid="welcome-header">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bienvenido, {user?.nombre}
            </h1>
            <p className="text-blue-200">
              Panel de control del Sistema de Recaudación Tributaria Municipal
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
            <Clock className="w-4 h-4" />
            <span className="text-sm">
              {new Date().toLocaleDateString('es-VE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stats-grid">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={index} 
              className={`card-hover ${getCardColor(stat.color)}`}
              data-testid={`stat-card-${index}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className={`p-3 rounded-lg ${getIconBg(stat.color)}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-medium ${stat.trendUp ? 'text-green-600' : 'text-red-600'}`}>
                    {stat.trendUp ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                    {stat.trend}
                  </div>
                </div>
                <div className="mt-4">
                  <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {stat.value}
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">{stat.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{stat.description}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Transactions */}
      <Card data-testid="recent-transactions">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Transacciones Recientes
              </CardTitle>
              <CardDescription>Últimos movimientos registrados en el sistema</CardDescription>
            </div>
            <button className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium">
              Ver todas
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">ID</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Contribuyente</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Concepto</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Monto</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Estado</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.map((tx, index) => (
                  <tr key={tx.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`transaction-row-${index}`}>
                    <td className="py-3 px-4 text-sm font-mono text-blue-600">{tx.id}</td>
                    <td className="py-3 px-4 text-sm text-slate-700 font-medium">{tx.contribuyente}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{tx.concepto}</td>
                    <td className="py-3 px-4 text-sm font-semibold text-slate-900 font-mono">{tx.monto}</td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className={`${getStatusBadge(tx.estado)} text-xs`}>
                        {tx.estado}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-500">{tx.fecha}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="quick-actions">
        <Card className="card-hover cursor-pointer group">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto group-hover:bg-blue-200 transition-colors">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <p className="mt-3 text-sm font-medium text-slate-700">Nuevo Contribuyente</p>
          </CardContent>
        </Card>
        <Card className="card-hover cursor-pointer group">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto group-hover:bg-green-200 transition-colors">
              <Calculator className="w-6 h-6 text-green-600" />
            </div>
            <p className="mt-3 text-sm font-medium text-slate-700">Nueva Liquidación</p>
          </CardContent>
        </Card>
        <Card className="card-hover cursor-pointer group">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto group-hover:bg-yellow-200 transition-colors">
              <FileText className="w-6 h-6 text-yellow-600" />
            </div>
            <p className="mt-3 text-sm font-medium text-slate-700">Solicitar LAE</p>
          </CardContent>
        </Card>
        <Card className="card-hover cursor-pointer group">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto group-hover:bg-purple-200 transition-colors">
              <Wallet className="w-6 h-6 text-purple-600" />
            </div>
            <p className="mt-3 text-sm font-medium text-slate-700">Registrar Pago</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardHome;
